import React, { useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import * as Icons from "lucide-react";

const KIND_LABEL = { client: "Client / family", external: "Partner / vendor / facility", professional: "Professional" };

// Staff "Remind Now" — preview → confirm → send a safe on-demand document nudge (PROMPT 13F).
export default function RemindNowDialog({ docId, variant = "outline", size = "sm", className = "", iconOnly = false, onSent }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pv, setPv] = useState(null);
  const [msg, setMsg] = useState("");
  const [confirm, setConfirm] = useState(false);

  const openDialog = async (e) => {
    e?.stopPropagation?.();
    setOpen(true); setLoading(true); setPv(null); setMsg(""); setConfirm(false);
    try { const { data } = await api.get(`/doc-center/documents/${docId}/reminder-preview`); setPv(data); setConfirm(!!data.already_reminded_today); }
    catch (err) { toast.error(errMsg(err)); setOpen(false); }
    finally { setLoading(false); }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/doc-center/documents/${docId}/remind`, { message: msg.trim() || undefined, confirm });
      toast.success(data.message || "Reminder sent.");
      setOpen(false); onSent?.();
    } catch (err) {
      if (err?.response?.status === 409) { setConfirm(true); toast.message("Already reminded in the last 24 hours — press Send again to confirm."); }
      else toast.error(errMsg(err));
    } finally { setSending(false); }
  };

  return (
    <>
      <Button variant={variant} size={size} className={className} data-testid={`remind-now-btn-${docId}`}
        onClick={openDialog} title="Remind Now">
        <Icons.BellRing size={15} className={iconOnly ? "" : "mr-1"} />{iconOnly ? "" : "Remind"}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!sending) setOpen(o); }}>
        <DialogContent data-testid="remind-now-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1C4358] flex items-center gap-2"><Icons.BellRing size={17} /> Remind Now</DialogTitle>
            <DialogDescription className="text-[13px]">Send a safe, on-demand reminder to the person who has a pending action on this document.</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-8 text-center text-[13px] text-[#6B7280]"><Icons.Loader2 className="animate-spin inline mr-2" size={15} /> Checking…</div>
          ) : !pv ? null : !pv.can_remind ? (
            <div className="py-6 text-center" data-testid="remind-cannot">
              <Icons.CheckCircle2 size={26} className="mx-auto text-[#2F7D5A] mb-2" />
              <p className="text-[14px] text-[#252221] font-medium">Nothing to remind about</p>
              <p className="text-[13px] text-muted-foreground mt-1">{pv.reason}</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="remind-preview">
              <div className="rounded-lg bg-[#F4F8FB] border border-[#E6EDF2] p-3 space-y-1.5">
                <div className="flex justify-between gap-3 text-[13px]"><span className="text-[#6B7280]">Recipient</span>
                  <span className="font-medium text-[#252221] text-right" data-testid="remind-recipient">{pv.recipient.name} <span className="text-[#94A3B8]">({KIND_LABEL[pv.recipient.kind] || pv.recipient.kind})</span></span></div>
                <div className="flex justify-between gap-3 text-[13px]"><span className="text-[#6B7280]">Email</span><span className="text-[#252221]">{pv.recipient.email || "—"}</span></div>
                <div className="flex justify-between gap-3 text-[13px]"><span className="text-[#6B7280]">Document</span><span className="font-medium text-[#252221] text-right">{pv.document_title}</span></div>
                <div className="flex justify-between gap-3 text-[13px]"><span className="text-[#6B7280]">Action needed</span><span className="font-medium text-[#9C1E22] text-right" data-testid="remind-action">Please {pv.action_label}</span></div>
              </div>

              {pv.disclaimer && (
                <div className="rounded-lg bg-[#FFF7ED] border border-[#FED7AA] px-3 py-2 flex items-start gap-2" data-testid="remind-disclaimer">
                  <Icons.ShieldAlert size={14} className="text-[#B7791F] shrink-0 mt-0.5" />
                  <span className="text-[12px] text-[#92600E]">{pv.disclaimer}</span>
                </div>
              )}

              {confirm && pv.already_reminded_today && (
                <div className="rounded-lg bg-[#FEF2F2] border border-[#FCA5A5] px-3 py-2 flex items-start gap-2" data-testid="remind-rate-warning">
                  <Icons.AlertTriangle size={14} className="text-[#9C1E22] shrink-0 mt-0.5" />
                  <span className="text-[12px] text-[#9C1E22]">This recipient was already reminded about this document in the last 24 hours. Send again only if needed.</span>
                </div>
              )}

              <div>
                <label className="text-[12px] text-[#6B7280]">Optional note (visible to the recipient)</label>
                <Textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} className="text-[13px] mt-1" maxLength={1000}
                  placeholder={pv.suggested_message} data-testid="remind-message" />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending} data-testid="remind-cancel">Cancel</Button>
            {pv?.can_remind && (
              <Button onClick={send} disabled={sending} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white" data-testid="remind-send">
                {sending ? <Icons.Loader2 className="animate-spin mr-1" size={14} /> : <Icons.Send size={14} className="mr-1" />}
                {confirm && pv.already_reminded_today ? "Send again" : "Send reminder"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
